import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { PREDICTION } from '@/tools/prediction/prediction.constants';
import { adjustPrincipalComponentsToKeep } from '@/tools/prediction/prediction.utilities';
import { BASE_TOOL_COMMON_PROPS, DEFAULT_TOOL_ITEM_PROPS } from '@/tools/tool.constants';
import {
  buildToolRehydrationState,
  getNextToolParameterUnsetValue,
  getNextToolParameterValue,
  getRequiredToolParameterDefinition,
} from '@/tools/tool.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { BaseToolPersistedState, ParameterDefinitions, ToolItem, ToolRehydratePayload } from '@/tools/tool.types';

type PredictionScale = (typeof PREDICTION.SCALE)[keyof typeof PREDICTION.SCALE];
type RegressionMethod = (typeof PREDICTION.REGRESSION_METHODS)[keyof typeof PREDICTION.REGRESSION_METHODS];

export interface PredictionPanelConfig {
  lambda: number;
  mustGoThroughZero: boolean;
  option: PredictionScale;
  polynomialValue?: number;
  windowStart: number;
  windowEnd: number;
  regressionMethod: RegressionMethod;
  variableSelection: number;
  variableSelectionEnabled: boolean;
  refreshRate: FrontendDuration;
  isRefreshRateEnabled: boolean;
  type: string;
}

export interface PredictionPanelState extends BaseToolPersistedState {
  targetSignal: ToolItem | undefined;
  condition: ToolItem | undefined;
  inputSignals: ToolItem[];
  option: PredictionScale;
  polynomialValue: number;
  mustGoThroughZero: boolean;
  lambda: number;
  windowStart: number;
  windowEnd: number;
  modelExpanded: boolean;
  regressionMethod: RegressionMethod;
  variableSelection: number;
  refreshRate: FrontendDuration;
  isRefreshRateEnabled: boolean;
  variableSelectionEnabled: boolean;
  principalComponentsToKeepByUser: number | null | undefined;
  color: string;
}

export type PredictionPanelDehydratedState = PredictionPanelState;

export interface PredictionPanelActions {
  setOption: (option: PredictionScale) => void;
  setPolynomialValue: (polynomialValue: number) => void;
  setMustGoThroughZero: (mustGoThroughZero: boolean) => void;
  setLambda: (lambda: number) => void;
  setPrincipalComponentsToKeepByUser: (principalComponentsToKeep: number | null) => void;
  setTrainingWindow: (window: { startTime: number; endTime: number }) => void;
  setModelExpanded: (modelExpanded: boolean) => void;
  setRegressionMethod: (regressionMethod: RegressionMethod) => void;
  setRefreshRate: (refreshRate: FrontendDuration) => void;
  setIsRefreshRateEnabled: (isRefreshRateEnabled: boolean) => void;
  setVariableSelection: (variableSelection: number) => void;
  setVariableSelectionEnabled: (variableSelectionEnabled: boolean) => void;
  setColor: (color: string) => void;
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsedState: (collapsed: boolean) => void;
  setTargetSignalForPrediction: (item: ToolItem | null | undefined) => void;
  setConditionForPrediction: (item: ToolItem | null | undefined) => void;
  setInputSignalsForPrediction: (item: ToolItem | null | undefined) => void;
  unsetInputSignalsForPrediction: (item: ToolItem | null | undefined) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  reset: (overrides?: Partial<PredictionPanelState>) => void;
}

export const parameterDefinitions: ParameterDefinitions = {
  targetSignal: { predicate: ['name', 'target'] },
  condition: { predicate: ['name', 'condition'] },
  inputSignals: {
    predicate: (parameter) => !['target', 'condition'].includes(parameter.name ?? ''),
    multiple: true,
  },
};

const initialState: PredictionPanelState = {
  ...BASE_TOOL_COMMON_PROPS,
  targetSignal: undefined,
  condition: undefined,
  inputSignals: [],
  option: PREDICTION.SCALE.LINEAR,
  polynomialValue: 2,
  mustGoThroughZero: false,
  lambda: 0,
  windowStart: 0,
  windowEnd: 0,
  modelExpanded: false,
  regressionMethod: PREDICTION.REGRESSION_METHODS.ORDINARY_LEAST_SQUARES,
  variableSelection: 0.05,
  refreshRate: { value: 30, units: 'min' },
  isRefreshRateEnabled: true,
  variableSelectionEnabled: false,
  principalComponentsToKeepByUser: undefined,
  color: '',
};

export const dehydratePredictionPanelState = (state: PredictionPanelState): PredictionPanelDehydratedState => state;

const predictionPanelActionsFactory: ActionsFactory<PredictionPanelState, PredictionPanelActions> = (
  set,
  _get,
  resetState,
) => ({
  setOption: (option) =>
    set((draft) => {
      draft.option = option;
    }),

  setPolynomialValue: (polynomialValue) =>
    set((draft) => {
      draft.polynomialValue = polynomialValue;
    }),

  setMustGoThroughZero: (mustGoThroughZero) =>
    set((draft) => {
      draft.mustGoThroughZero = mustGoThroughZero;
    }),

  setLambda: (lambda) =>
    set((draft) => {
      draft.lambda = lambda;
    }),

  setPrincipalComponentsToKeepByUser: (principalComponentsToKeep) =>
    set((draft) => {
      draft.principalComponentsToKeepByUser = principalComponentsToKeep;
    }),

  setTrainingWindow: (window) =>
    set((draft) => {
      draft.windowStart = window.startTime;
      draft.windowEnd = window.endTime;
    }),

  setModelExpanded: (modelExpanded) =>
    set((draft) => {
      draft.modelExpanded = modelExpanded;
    }),

  setRegressionMethod: (regressionMethod) =>
    set((draft) => {
      draft.regressionMethod = regressionMethod;
    }),

  setRefreshRate: (refreshRate) =>
    set((draft) => {
      draft.refreshRate = refreshRate;
    }),

  setIsRefreshRateEnabled: (isRefreshRateEnabled) =>
    set((draft) => {
      draft.isRefreshRateEnabled = isRefreshRateEnabled;
    }),

  setVariableSelection: (variableSelection) =>
    set((draft) => {
      draft.variableSelection = variableSelection;
    }),

  setVariableSelectionEnabled: (variableSelectionEnabled) =>
    set((draft) => {
      draft.variableSelectionEnabled = variableSelectionEnabled;
    }),

  setColor: (color) =>
    set((draft) => {
      draft.color = color;
    }),

  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsedState: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setTargetSignalForPrediction: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(parameterDefinitions, 'targetSignal');
      const nextValue = getNextToolParameterValue({
        definition,
        currentValue: draft.targetSignal,
        item,
        toolItemProps: DEFAULT_TOOL_ITEM_PROPS,
      });
      if (nextValue.changed) {
        draft.targetSignal = nextValue.value as ToolItem | undefined;
      }
    }),

  setConditionForPrediction: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(parameterDefinitions, 'condition');
      const nextValue = getNextToolParameterValue({
        definition,
        currentValue: draft.condition,
        item,
        toolItemProps: DEFAULT_TOOL_ITEM_PROPS,
      });
      if (nextValue.changed) {
        draft.condition = nextValue.value as ToolItem | undefined;
      }
    }),

  setInputSignalsForPrediction: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(parameterDefinitions, 'inputSignals');
      const nextValue = getNextToolParameterValue({
        definition,
        currentValue: draft.inputSignals,
        item,
        toolItemProps: DEFAULT_TOOL_ITEM_PROPS,
      });
      if (nextValue.changed) {
        draft.inputSignals = nextValue.value as ToolItem[];
      }
    }),

  unsetInputSignalsForPrediction: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(parameterDefinitions, 'inputSignals');
      const nextValue = getNextToolParameterUnsetValue({ definition, currentValue: draft.inputSignals, item });
      if (nextValue.changed) {
        draft.inputSignals = nextValue.value as ToolItem[];
      }
    }),

  rehydrateForEdit: (payload) => {
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });
      Object.assign(draft, initialState, rehydrationState.config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
      });
    });
  },

  reset: (overrides) => resetState(overrides),
});

const STORE_NAME = 'Prediction';

const [PredictionPanelStoreProvider, usePredictionPanelStoreInstance, usePredictionPanelSelector] = createStore(
  STORE_NAME,
  initialState,
  predictionPanelActionsFactory,
);

export { PredictionPanelStoreProvider };

export const usePredictionId = () => usePredictionPanelSelector((state) => state.id);
export const usePredictionName = () => usePredictionPanelSelector((state) => state.name);
export const usePredictionOriginalParameters = () => usePredictionPanelSelector((state) => state.originalParameters);
export const usePredictionAdvancedParametersCollapsed = () =>
  usePredictionPanelSelector((state) => state.advancedParametersCollapsed);
export const usePredictionTargetSignal = () => usePredictionPanelSelector((state) => state.targetSignal);
export const usePredictionCondition = () => usePredictionPanelSelector((state) => state.condition);
export const usePredictionInputSignals = () => usePredictionPanelSelector((state) => state.inputSignals);
export const usePredictionOption = () => usePredictionPanelSelector((state) => state.option);
export const usePredictionPolynomialValue = () => usePredictionPanelSelector((state) => state.polynomialValue);
export const usePredictionMustGoThroughZero = () => usePredictionPanelSelector((state) => state.mustGoThroughZero);
export const usePredictionLambda = () => usePredictionPanelSelector((state) => state.lambda);
export const usePredictionModelExpanded = () => usePredictionPanelSelector((state) => state.modelExpanded);
export const usePredictionRegressionMethod = () => usePredictionPanelSelector((state) => state.regressionMethod);
export const usePredictionVariableSelection = () => usePredictionPanelSelector((state) => state.variableSelection);
export const usePredictionRefreshRate = () => usePredictionPanelSelector((state) => state.refreshRate);
export const usePredictionIsRefreshRateEnabled = () =>
  usePredictionPanelSelector((state) => state.isRefreshRateEnabled);
export const usePredictionVariableSelectionEnabled = () =>
  usePredictionPanelSelector((state) => state.variableSelectionEnabled);
export const usePredictionColor = () => usePredictionPanelSelector((state) => state.color);

const selectTrainingWindow = createSelector(
  (state: PredictionPanelState) => state.windowStart,
  (state: PredictionPanelState) => state.windowEnd,
  (startTime, endTime) => ({ startTime, endTime }),
);
/** The reference training window, derived from `windowStart`/`windowEnd`. */
export const usePredictionTrainingWindow = () => usePredictionPanelSelector(selectTrainingWindow);

/** Pure function backing `usePrincipalComponentsToKeep`; also used for non-hook reads (e.g. action files). */
export function computePrincipalComponentsToKeep(state: PredictionPanelState): number {
  return adjustPrincipalComponentsToKeep(
    state.inputSignals,
    state.option,
    state.polynomialValue,
    state.principalComponentsToKeepByUser,
  );
}

const selectPrincipalComponentsToKeep = createSelector(
  (state: PredictionPanelState) => state,
  computePrincipalComponentsToKeep,
);
export const usePredictionPrincipalComponentsToKeep = () => usePredictionPanelSelector(selectPrincipalComponentsToKeep);

/** Pure function backing `usePredictionConfig`; also used for non-hook reads (e.g. action files). */
export function computePredictionConfig(state: PredictionPanelState): PredictionPanelConfig {
  return {
    lambda: state.lambda,
    mustGoThroughZero: state.mustGoThroughZero,
    option: state.option,
    polynomialValue: state.polynomialValue,
    windowStart: state.windowStart,
    windowEnd: state.windowEnd,
    regressionMethod: state.regressionMethod,
    variableSelection: state.variableSelection,
    variableSelectionEnabled: state.variableSelectionEnabled,
    refreshRate: state.refreshRate,
    isRefreshRateEnabled: state.isRefreshRateEnabled,
    type: TREND_TOOLS.PREDICTION,
  };
}

const selectPredictionConfig = createSelector((state: PredictionPanelState) => state, computePredictionConfig);
export const usePredictionConfig = () => usePredictionPanelSelector(selectPredictionConfig);

export const usePredictionPanelActions = () => usePredictionPanelStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (e.g. `predictionPanel.actions.ts`,
 * `investigate.actions.ts`'s edit-rehydration dispatch) that cannot use hooks. New code should use the hooks
 * above and the Provider.
 */
export function legacyCreatePredictionPanelStore() {
  return createStoreInstance<PredictionPanelState, PredictionPanelActions>(
    initialState,
    predictionPanelActionsFactory,
    { storeName: STORE_NAME },
  );
}

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<PredictionPanelStoreProvider>` with UI-driven assertions instead.
 */
export function createTestPredictionPanelStore(overrides?: Partial<PredictionPanelState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestPredictionPanelStore() should only be used in tests.');
  }
  return createStoreInstance({ ...initialState, ...overrides }, predictionPanelActionsFactory);
}

export const usePersistPredictionPanelStore = () => {
  const register = useRegisterPersistedStore();
  const store = usePredictionPanelStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqPredictionPanelStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydratePredictionPanelState,
    });
  }, [register, store]);
};
